// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadQuotes from "@insite/client-framework/Store/Data/Quotes/Handlers/LoadQuotes";
import { getQuotesDataView } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import { CardListStyles } from "@insite/content-library/Components/CardList";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import Skeleton from "@vanmeter/App/Components/Skeleton/Skeleton";
import { CustomAppState } from "@vanmeter/App/Store/Reducers";
import QuoteSummaryCard, {
    QuoteSummaryCardStyles,
    RecentQuoteTableProperties,
} from "@vanmeter/Overrides/Components/QuoteSummaryCard";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

// <XNG-Changes>
const enum fields {
    numberOfRecords = "numberOfRecords",
    showQuoteNumber = "showQuoteNumber",
    showType = "showType",
    showStatus = "showStatus",
    showPoNumber = "showPoNumber",
    showRequested = "showRequested",
    showShipTo = "showShipTo",
    showExpires = "showExpires",
    showSalesRep = "showSalesRep",
    showUser = "showUser",
    showCustomer = "showCustomer",
    showSubTotal = "showSubTotal",
    showActions = "showActions",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
        [fields.showQuoteNumber]: boolean;
        [fields.showType]: boolean;
        [fields.showStatus]: boolean;
        [fields.showPoNumber]: boolean;
        [fields.showRequested]: boolean;
        [fields.showShipTo]: boolean;
        [fields.showExpires]: boolean;
        [fields.showSalesRep]: boolean;
        [fields.showUser]: boolean;
        [fields.showCustomer]: boolean;
        [fields.showSubTotal]: boolean;
        [fields.showActions]: boolean;
    };
}
// </XNG-Changes>

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    return {
        settingsCollection: getSettingsCollection(state),
        session,
        quotesDataView: getQuotesDataView(state, recentQuotesParameter),
        myQuotesPageNavLink: getPageLinkByPageType(state, "RfqMyQuotesPage"),
        canViewQuotes: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
        // XNG-Change
        tabs: (state as CustomAppState).xTabs,
    };
};

const mapDispatchToProps = {
    loadQuotes,
    // XNG-Change
    updateZone: (zone: string, enabled: boolean) => ({
        type: "xTabs/EnableZone",
        data: {
            zone,
            isEnabled: enabled, // we need to show this tab if we are in tab context
        },
    }),
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentQuotesStyles {
    cardList?: CardListStyles;
    noQuotesGridItem?: GridItemProps;
    noQuotesText?: TypographyPresentationProps;
    cardContainer?: CardContainerStyles;
    quoteSummaryCard?: QuoteSummaryCardStyles;
    // XNG-Changes
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableHeaders?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    dataTableCells?: DataTableCellBaseProps;
    dataCell?: DataTableCellProps;
    viewAllLinkStyle?: LinkPresentationProps;
}

export const recentQuotesStyles: RecentQuotesStyles = {
    noQuotesGridItem: { width: 12 },
};

const recentQuotesParameter = {
    page: 1,
    pageSize: 5,
};

const styles = recentQuotesStyles;

class RecentQuotes extends React.Component<Props> {
    componentDidMount() {
        if (this.props.canViewQuotes && !this.props.quotesDataView.value) {
            // XNG-Changes
            recentQuotesParameter.pageSize = this.props.fields.numberOfRecords || recentQuotesParameter.pageSize;
            this.props.loadQuotes(recentQuotesParameter);
        }
    }

    // XNG-change
    componentDidUpdate(): void {
        if (this.props.zone?.includes("tab")) {
            if (this.props.canViewQuotes && this.props.quotesDataView.value && !this.props.tabs[this.props.zone]) {
                this.props.updateZone(this.props.zone, true);
            } else if (!this.props.canViewQuotes || !this.props.quotesDataView.value) {
                this.props.updateZone(this.props.zone, false);
            }
        }
    }

    render() {
        const { settingsCollection, session, quotesDataView, myQuotesPageNavLink, canViewQuotes } = this.props;
        // XNG-Changes
        if (canViewQuotes && quotesDataView.isLoading) {
            return <Skeleton height="260px" />;
        }
        if (!canViewQuotes || !quotesDataView.value) {
            return null;
        }

        const myQuotesPageUrl = myQuotesPageNavLink ? myQuotesPageNavLink.url : undefined;

        // XNG-Changes
        const tableProperties: RecentQuoteTableProperties = {
            quoteNumber: {
                show: this.props.fields.showQuoteNumber,
                title: translate("Quote #"),
            },
            type: {
                show: this.props.fields.showType && settingsCollection.quoteSettings.jobQuoteEnabled,
                title: translate("Type"),
            },
            status: {
                show: this.props.fields.showStatus,
                title: translate("Status"),
            },
            poNumber: {
                show: this.props.fields.showPoNumber,
                title: translate("Ref #"),
            },
            requested: {
                show: this.props.fields.showRequested,
                title: translate("Requested"),
            },
            expires: {
                show: this.props.fields.showExpires,
                title: translate("Expires"),
            },
            salesRep: {
                show: this.props.fields.showSalesRep && session.isSalesPerson,
                title: translate("Sales Rep"),
            },
            user: {
                show: this.props.fields.showUser && session.isSalesPerson,
                title: translate("User"),
            },
            customer: {
                show: this.props.fields.showCustomer,
                title: translate("Customer"),
            },
            subTotal: {
                show: this.props.fields.showSubTotal,
                title: translate("Sub-Total"),
            },
            shipTo: {
                show: this.props.fields.showShipTo && !session.isSalesPerson,
                title: translate("Ship To/Pick Up"),
            },
            actions: {
                show: this.props.fields.showActions,
                title: translate("PDF"),
            },
        };

        return (
            <StyledWrapper>
                {quotesDataView.value.length === 0 && (
                    <Typography {...styles.noQuotesText}>{translate("No Quotes found")}</Typography>
                )}
                {quotesDataView.value.length > 0 && (
                    <DataTable {...styles.dataTable}>
                        <DataTableHead {...styles.dataTableHead}>
                            {Object.keys(tableProperties).map(key => {
                                const { show, title } = (tableProperties as any)[key];
                                return (
                                    show && (
                                        <DataTableHeader key={key} {...styles.dataTableHeaders}>
                                            {title}
                                        </DataTableHeader>
                                    )
                                );
                            })}
                        </DataTableHead>
                        <DataTableBody {...styles.dataTableBody}>
                            {quotesDataView.value.map(quote => (
                                <QuoteSummaryCard
                                    key={quote.id}
                                    quote={quote}
                                    session={session}
                                    quoteSettings={settingsCollection.quoteSettings}
                                    extendedStyles={styles.quoteSummaryCard}
                                    // XNG-Changes
                                    tableProperties={tableProperties}
                                />
                            ))}
                        </DataTableBody>
                    </DataTable>
                )}
                {/*  XNG-Changes: View All Link */}
                <Link
                    href={myQuotesPageUrl}
                    {...styles.viewAllLinkStyle}
                    // XNG-Changes
                    aria-label={translate("View Full Quotes History")}
                >
                    {translate("View Full Quotes History")}
                </Link>
            </StyledWrapper>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentQuotes),
    definition: {
        group: "Common",
        icon: "list-ul",
        // XNG-Changes
        fieldDefinitions: [
            {
                name: fields.numberOfRecords,
                displayName: "Number of Recent Quotes Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 5,
                fieldType: "General",
                sortOrder: 1,
                isRequired: true,
            },
            {
                name: fields.showQuoteNumber,
                displayName: "Quote #",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.showType,
                displayName: "Type",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.showStatus,
                displayName: "Status",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showRequested,
                displayName: "Requested",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showShipTo,
                displayName: "Ship To/Pick Up",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.showExpires,
                displayName: "Expires",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 6,
            },
            {
                name: fields.showSalesRep,
                displayName: "Sales Rep",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 7,
            },
            {
                name: fields.showCustomer,
                displayName: "Customer",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 8,
            },
            {
                name: fields.showSubTotal,
                displayName: "subTotal",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 9,
            },
            {
                name: fields.showUser,
                displayName: "User",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 10,
            },
            {
                name: fields.showPoNumber,
                displayName: "PO Number",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 11,
            },
            {
                name: fields.showActions,
                displayName: "Actions",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 12,
            },
        ],
    },
};

export default widgetModule;
